import React from 'react';

import { useIntl } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { pasteOptionsToolbarMessages } from '@atlaskit/editor-common/messages';
import {
	useEditorToolbar,
	AI_PASTE_MENU_SECTION,
	PASTE_MENU,
	PASTE_MENU_RANK,
} from '@atlaskit/editor-common/toolbar';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { PasteOptionsToolbarSharedState } from '@atlaskit/editor-plugin-paste-options-toolbar';
import type { Slice } from '@atlaskit/editor-prosemirror/model';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import {
	AICommandIcon,
	AIShortenIcon,
	AISpellcheckIcon,
	ToolbarDropdownItem,
	ToolbarDropdownItemSection,
} from '@atlaskit/editor-toolbar';
import type {
	AsyncHiddenContext,
	RegisterComponent,
} from '@atlaskit/editor-ui-control-model/types';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import TextLengthenIcon from '@atlaskit/icon-lab/core/text-lengthen';
import type { JsonLd } from '@atlaskit/json-ld-types/jsonld';
import type { CardContext } from '@atlaskit/link-provider/context';
import { RovoIcon } from '@atlaskit/logo';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import {
	fixSpellingAndGrammar,
	improveWriting,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { freeGenerate } from '../prebuilt/config-items/prompts/free-generate/free-generate';
import { makeLonger } from '../prebuilt/config-items/prompts/make-longer/make-longer';
import { makeShorter } from '../prebuilt/config-items/prompts/make-shorter/make-shorter';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';

import { SmartLinkAddSummaryPasteMenuItem } from './smart-link-add-summary-paste-menu-item';

const addPolishWithImproveFormatting = () => {
	const item = addPolish();
	// addPolish() already sets the title to improveFormattingTitle; only the description differs here.
	item.description = aiConfigItemMessages.improveFormattingDescription;
	return item;
};

type AIPasteMenuItemProps = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
	icon: React.ReactNode;
	testId: string;
};

const AIPasteMenuItem = ({
	aiGlobalOptIn,
	api,
	configItem,
	icon,
	testId,
}: AIPasteMenuItemProps) => {
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();

	const { pasteStartPos, pasteEndPos } = useSharedPluginStateWithSelector(
		api,
		['pasteOptionsToolbarPlugin'],
		(states) => {
			const pluginState = states.pasteOptionsToolbarPluginState as
				| PasteOptionsToolbarSharedState
				| undefined;
			return {
				pasteStartPos: pluginState?.pasteStartPos ?? 0,
				pasteEndPos: pluginState?.pasteEndPos ?? 0,
			};
		},
	);

	const label = formatMessage(configItem.title);

	return (
		<ToolbarDropdownItem
			testId={testId}
			elemBefore={icon}
			/* eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed) */
			onClick={(event) => {
				if (!editorView) {
					return;
				}
				const isKeyboardTrigger = 'key' in event;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
				const docSize = editorView.state.doc.content.size;
				const nodeAtPasteStartNode =
					pasteStartPos >= 0 && pasteStartPos <= docSize
						? editorView.state.doc.nodeAt(pasteStartPos)
						: undefined;
				const hasValidOverrideRange =
					pasteStartPos >= 0 &&
					pasteStartPos <= docSize &&
					pasteEndPos >= pasteStartPos &&
					pasteEndPos <= docSize;
				let safeOverrideFromPos = pasteStartPos;
				let safeOverrideToPos = pasteEndPos;
				if (!hasValidOverrideRange) {
					if (nodeAtPasteStartNode) {
						safeOverrideFromPos = pasteStartPos;
						safeOverrideToPos = Math.min(pasteStartPos + nodeAtPasteStartNode.nodeSize, docSize);
					} else {
						safeOverrideFromPos = editorView.state.selection.from;
						safeOverrideToPos = editorView.state.selection.to;
					}
				}

				// Set the editor selection to the pasted content range before opening the
				// AI modal. This ensures that Rovo uses the pasted content as context
				// rather than the entire page, matching the behaviour of the smartlink
				// toolbar version. See: open-remix-modal.ts for the same pattern.
				const pasteSelection = TextSelection.create(
					editorView.state.doc,
					safeOverrideFromPos,
					safeOverrideToPos,
				);
				const selectionTr = editorView.state.tr.setSelection(pasteSelection);
				editorView.dispatch(selectionTr);

				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem,
					lastTriggeredFrom: 'pasteActionsMenu',
					triggerMethod,
					aiGlobalOptIn,
					locale,
					overrideFromPos: safeOverrideFromPos,
					overrideToPos: safeOverrideToPos,
					shouldExpandPositions: false,
				});
				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{label}
		</ToolbarDropdownItem>
	);
};

const AIPasteMenuSection = ({ children }: { children?: React.ReactNode }) => {
	const intl = useIntl();
	return (
		<ToolbarDropdownItemSection
			title={intl.formatMessage(pasteOptionsToolbarMessages.pasteMenuActionsTitle)}
		>
			<div role="menu">{children}</div>
		</ToolbarDropdownItemSection>
	);
};

export const AI_PASTE_MENU_KEY = 'ai-paste-menu';
const AI_PASTE_MENU_SECTION_KEY = AI_PASTE_MENU_SECTION.key;
const AI_PASTE_MENU_IMPROVE_WRITING_KEY = 'ai-paste-menu-improve-writing';
const AI_PASTE_MENU_FIX_SPELLING_KEY = 'ai-paste-menu-fix-spelling-and-grammar';
const AI_PASTE_MENU_ADD_POLISH_KEY = 'ai-paste-menu-add-polish';
const AI_PASTE_MENU_MAKE_SHORTER_KEY = 'ai-paste-menu-make-shorter';
const AI_PASTE_MENU_MAKE_LONGER_KEY = 'ai-paste-menu-make-longer';
const AI_PASTE_MENU_REMIX_KEY = 'ai-paste-menu-remix';
const AI_PASTE_MENU_ADD_SUMMARY_KEY = 'ai-paste-menu-add-summary';
const AI_PASTE_MENU_ASK_ROVO_KEY = 'ai-paste-menu-ask-rovo';

type AIPasteMenuButtonConfig = {
	button: string;
	charMaxThreshold?: number;
	charMinThreshold: number;
	rank: number;
};

export const AI_PASTE_MENU_DISABLED_IN_NODES: string[] = ['codeBlock', 'heading'];

/**
 * Returns the active excluded nodes list, preferring the value from the `excludedNodes`
 * experiment param when it is non-empty and contains a valid string[]. Falls back to
 * `AI_PASTE_MENU_DISABLED_IN_NODES` otherwise.
 */
export const getActiveDisabledInNodes = (): string[] => {
	const raw = expValNoExposure('platform_editor_paste_actions_menu', 'excludedNodes', '');
	if (!raw) {
		return AI_PASTE_MENU_DISABLED_IN_NODES;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every((n) => typeof n === 'string')) {
			return parsed as string[];
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_DISABLED_IN_NODES;
};

export const AI_PASTE_MENU_CONFIG: AIPasteMenuButtonConfig[] = [
	{
		button: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
		charMinThreshold: 100,
		rank: 2,
	},
	{
		button: AI_PASTE_MENU_FIX_SPELLING_KEY,
		charMinThreshold: 100,
		rank: 3,
	},
	{
		button: AI_PASTE_MENU_MAKE_SHORTER_KEY,
		charMinThreshold: 100,
		charMaxThreshold: 600,
		rank: 4,
	},
	{
		button: AI_PASTE_MENU_ADD_POLISH_KEY,
		charMinThreshold: 601,
		rank: 1,
	},
];

const isAIPasteMenuButtonConfig = (value: unknown): value is AIPasteMenuButtonConfig =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as AIPasteMenuButtonConfig).button === 'string' &&
	typeof (value as AIPasteMenuButtonConfig).charMinThreshold === 'number' &&
	(typeof (value as AIPasteMenuButtonConfig).charMaxThreshold === 'number' ||
		(value as AIPasteMenuButtonConfig).charMaxThreshold === undefined) &&
	typeof (value as AIPasteMenuButtonConfig).rank === 'number';

/**
 * Returns the active menu config, preferring the value from the `menuConfig` experiment
 * param when it is non-empty and contains a valid `AIPasteMenuButtonConfig[]`. Falls back
 * to the hardcoded `AI_PASTE_MENU_CONFIG` otherwise.
 */
export const getActiveMenuConfig = (): AIPasteMenuButtonConfig[] => {
	const raw = expValNoExposure('platform_editor_paste_actions_menu', 'menuConfig', '');
	if (!raw) {
		return AI_PASTE_MENU_CONFIG;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		if (Array.isArray(parsed) && parsed.length > 0 && parsed.every(isAIPasteMenuButtonConfig)) {
			return parsed;
		}
	} catch {
		// Invalid JSON – fall through to default.
	}
	return AI_PASTE_MENU_CONFIG;
};

/**
 * Returns true when the pasted text does not look like prose — i.e. it contains
 * no whitespace AND is entirely ASCII characters (after trimming).
 *
 * Text without spaces that is all-ASCII is very likely a URL, file path, token,
 * hash, or variable name — content where AI writing suggestions (improve writing,
 * fix spelling, etc.) would not be useful.
 *
 * We require all-ASCII because many natural languages (Chinese, Japanese, Thai,
 * Lao, Khmer, etc.) do not use spaces between words, so a no-whitespace check
 * alone would incorrectly flag those as non-prose.
 */
const hasTableNode = (slice: Slice | undefined): boolean => {
	if (!slice) {
		return false;
	}
	let found = false;
	slice.content.descendants((node) => {
		if (node.type.name === 'table') {
			found = true;
			return false;
		}
		return true;
	});
	return found;
};

export const isNotProse = (text: string): boolean => {
	const trimmed = text.trim();
	if (!trimmed) {
		return false;
	}

	// Check each character: if we find whitespace it's prose-like,
	// if we find a non-ASCII character it's likely CJK/Thai/etc.
	for (let i = 0; i < trimmed.length; i++) {
		const code = trimmed.charCodeAt(i);
		// Whitespace (space, tab, newline, etc.) → prose-like
		if (code === 0x20 || code === 0x09 || code === 0x0a || code === 0x0d) {
			return false;
		}
		// Non-ASCII character → likely a non-Latin script (CJK, Thai, etc.)
		if (code > 0x7f) {
			return false;
		}
	}

	// No whitespace and all ASCII → URL, token, path, etc.
	return true;
};

export const getAIPasteMenuComponents = ({
	aiGlobalOptIn,
	api,
	getAppearance: getAppearanceProp,
	appearance: appearanceAtRegistration,
	product,
	smartCardClientRef,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	/**
	 * Static editor appearance.  Prefer passing `getAppearance` when the
	 * appearance can change after registration (e.g. toggling between
	 * full-page and comment in an example).
	 */
	appearance?: EditorAppearance;
	/**
	 * Optional getter that returns the current editor appearance lazily on
	 * every rule evaluation.  Preferred over the static `appearance` param
	 * because `appearance` may be `undefined` at plugin-registration time
	 * (before the editor is fully mounted).
	 *
	 * When both are supplied `getAppearance` takes precedence.
	 * When neither is supplied the static `appearance` value is used.
	 */
	getAppearance?: () => EditorAppearance | undefined;
	product: string;
	/**
	 * Populated once the SmartCard context mounts.
	 */
	smartCardClientRef?: { current: CardContext['connections']['client'] | undefined };
}): RegisterComponent[] => {
	/**
	 * Hides the component when the pasted URL is inaccessible.
	 * Falls back to hidden when the SmartCard client is not available.
	 */
	const smartlinkAsyncHidden = async (context: AsyncHiddenContext) => {
		const { pastedUrl } = context;
		if (!pastedUrl) {
			return false;
		}

		const isEnabled = expValEqualsNoExposure(
			'confluence_editor_paste_3p_link_actions_menu',
			'isEnabled',
			true,
		);
		const smartCardClient = smartCardClientRef?.current;
		if (!isEnabled || !smartCardClient) {
			return true;
		}

		try {
			const response: JsonLd.Response = await smartCardClient.fetchData(pastedUrl);
			return response.meta?.access !== 'granted';
		} catch {
			// Network / auth errors → hide
			return true;
		}
	};

	const getAppearance = (): EditorAppearance | undefined =>
		getAppearanceProp?.() ??
		api?.core?.sharedState.currentState()?.appearance ??
		appearanceAtRegistration;
	const activeConfig = getActiveMenuConfig();

	const getPlaintextLength = () => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.plaintextLength ?? 0;
	};

	const getAncestorNodeNames = (): string[] => {
		const state = api?.pasteOptionsToolbarPlugin?.sharedState.currentState() as
			| PasteOptionsToolbarSharedState
			| undefined;
		return state?.pasteAncestorNodeNames ?? [];
	};

	const getPastedText = (): string | undefined => {
		const state = api?.paste?.sharedState.currentState();
		return state?.lastContentPasted?.text;
	};

	const getPastedSlice = () => {
		const state = api?.paste?.sharedState.currentState();
		return state?.lastContentPasted?.pastedSlice;
	};

	const configByKey = Object.fromEntries(activeConfig.map((c) => [c.button, c]));

	/** V1 (pre-rules) visibility check. Pure config + shared-state logic. */
	const makeIsHidden = (key: string) => () => {
		const config = configByKey[key];
		if (!config) {
			return true;
		}
		const length = getPlaintextLength();
		const ancestorNodeNames = getAncestorNodeNames();
		const belowMin = length < config.charMinThreshold;
		const aboveMax = config.charMaxThreshold !== undefined && length > config.charMaxThreshold;
		const inDisabledNode = ancestorNodeNames.some((name) =>
			getActiveDisabledInNodes().includes(name),
		);
		const pastedText = getPastedText();
		const notProse = pastedText !== undefined && isNotProse(pastedText);
		const containsTable = hasTableNode(getPastedSlice());
		return belowMin || aboveMax || inDisabledNode || notProse || containsTable;
	};

	/**
	 * V2-capable isHidden factory — the single place where the v2 rules
	 * registry is consulted.
	 *
	 * Accepts the same `key` string as `makeIsHidden` and encapsulates all
	 * per-button rules logic internally.  When the v2 experiment gate is on
	 * and the rules registry is available, the appropriate rule set for `key`
	 * is used.  When v2 is off (or rules are unavailable), falls back to
	 * `makeIsHidden(key)` for buttons that existed in v1, or `() => true` for
	 * new v2-only buttons (Make Longer, Remix, Ask Rovo).
	 */
	const rules = ['hasAltAiActions', 'hasSpellingAndGrammar'].includes(
		expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control'),
	)
		? api?.pasteOptionsToolbarPlugin?.actions.getPasteMenuRules()
		: undefined;

	const makeIsHiddenV2 = (key: string): (() => boolean) => {
		if (!rules) {
			// v2 off — fall back to v1 for buttons that existed pre-v2.
			const v1Keys = new Set([
				AI_PASTE_MENU_IMPROVE_WRITING_KEY,
				AI_PASTE_MENU_FIX_SPELLING_KEY,
				AI_PASTE_MENU_ADD_POLISH_KEY,
				AI_PASTE_MENU_MAKE_SHORTER_KEY,
			]);
			return v1Keys.has(key) ? makeIsHidden(key) : () => true;
		}

		const {
			allRules,
			minCharsRule,
			maxCharsRule,
			excludedAncestorRule,
			notProseRule,
			containsTableRule,
			isSingleLinkRule,
			notSingleLinkRule,
			hideIfSingleNodeRule,
			hideIfMixedNodesRule,
		} = rules;

		const disabled = excludedAncestorRule(getActiveDisabledInNodes());

		// isComment and variant must be re-evaluated on every isHidden() call so
		// that appearance changes are reflected without re-registering components.
		const getIsComment = () => getAppearance() === 'comment';
		const getVariant = () =>
			expValNoExposure('platform_editor_paste_actions_menu_v2', 'variant', 'control');

		switch (key) {
			case AI_PASTE_MENU_IMPROVE_WRITING_KEY:
				if (product === 'CONFLUENCE') {
					return () => {
						const isComment = getIsComment();
						const variant = getVariant();
						if (isComment) {
							// Confluence Comments:
							// Story 5.1 (single-node, medium, hasAltAiActions): show
							// Story 5.2 (mixed-node, medium, hasSpellingAndGrammar): show (replaces Remix)
							// Story 5.2 (mixed-node, medium, hasAltAiActions): show
							// Story 5.4 (mixed-node, large, hasAltAiActions): show (replaces Remix)
							if (['hasAltAiActions'].includes(variant)) {
								// hasAltAiActions: single-node medium + mixed-node medium/large
								return allRules(
									minCharsRule(100),
									isSingleLinkRule,
									disabled,
									notProseRule,
									containsTableRule,
								)();
							} else if (['hasSpellingAndGrammar'].includes(variant)) {
								// hasSpellingAndGrammar: single-node medium + mixed-node medium (replaces Remix).
								// Story 5.1 + 5.2: 100–599 chars only (large content shows Fix Spelling instead).
								return allRules(
									minCharsRule(100),
									maxCharsRule(599),
									isSingleLinkRule,
									disabled,
									notProseRule,
									containsTableRule,
								)();
							}
							return true;
						}
						// Non-comment Confluence (full-page etc.)
						if (['hasAltAiActions'].includes(variant)) {
							// Story 5.1 (single-node, medium): show
							// Story 5.2 (mixed-node, medium): show
							// Story 5.3 (single-node, large): show
							// Story 5.4 (mixed-node, large): hide (Remix takes over)
							return (
								minCharsRule(100)() ||
								(hideIfMixedNodesRule() && maxCharsRule(599)()) ||
								isSingleLinkRule() ||
								disabled() ||
								notProseRule() ||
								containsTableRule()
							);
						}
						// hasSpellingAndGrammar non-comment: single-node only (mixed-node defers to Fix Spelling).
						return allRules(
							minCharsRule(100),
							maxCharsRule(601),
							hideIfMixedNodesRule,
							isSingleLinkRule,
							disabled,
							notProseRule,
							containsTableRule,
						)();
					};
				}
				return () => true;

			case AI_PASTE_MENU_FIX_SPELLING_KEY:
				if (product === 'CONFLUENCE') {
					return () => {
						const isComment = getIsComment();
						const variant = getVariant();
						if (!['hasSpellingAndGrammar'].includes(variant)) {
							return true;
						}
						if (isComment) {
							// Confluence Comments hasSpellingAndGrammar:
							// Story 5.1 (single-node, medium): show
							// Story 5.2 (mixed-node, medium): show (replaces Remix)
							// Story 5.3 (single-node, large): show
							// Story 5.4 (mixed-node, large): show (replaces Remix)
							// → no node-type restriction, no max char limit
							return allRules(
								minCharsRule(configByKey[key]?.charMinThreshold ?? 100),
								isSingleLinkRule,
								disabled,
								notProseRule,
								containsTableRule,
							)();
						}
						// Non-comment Confluence (full-page etc.):
						// Medium mixed-node (100–599): Fix Spelling shown.
						// Large mixed-node (≥600): Fix Spelling hidden (Ask Rovo takes over).
						return (
							minCharsRule(configByKey[key]?.charMinThreshold ?? 100)() ||
							(hideIfMixedNodesRule() && !minCharsRule(600)()) ||
							isSingleLinkRule() ||
							disabled() ||
							notProseRule() ||
							containsTableRule()
						);
					};
				}
				return () => true;

			case AI_PASTE_MENU_ADD_POLISH_KEY:
				if (product === 'CONFLUENCE') {
					return allRules(
						minCharsRule(100),
						isSingleLinkRule,
						disabled,
						notProseRule,
						containsTableRule,
					);
				}
				return () => true;

			case AI_PASTE_MENU_MAKE_SHORTER_KEY:
				// Hidden in v2.
				return () => true;

			case AI_PASTE_MENU_MAKE_LONGER_KEY:
				if (product === 'CONFLUENCE') {
					return () => {
						const isComment = getIsComment();
						const variant = getVariant();
						if (['hasAltAiActions'].includes(variant)) {
							if (isComment) {
								// Confluence Comments hasAltAiActions:
								// Story 5.1 (single-node, medium): Make Longer shown
								// Story 5.2 (mixed-node, medium): Make Longer shown (replaces Remix)
								// → both single and mixed-node, medium only (< 600)
								return allRules(
									minCharsRule(100),
									maxCharsRule(599),
									isSingleLinkRule,
									disabled,
									notProseRule,
									containsTableRule,
								)();
							}
							// Non-comment Confluence: Story 5.1 — single-node medium only.
							return allRules(
								minCharsRule(100),
								maxCharsRule(599),
								hideIfMixedNodesRule,
								isSingleLinkRule,
								disabled,
								notProseRule,
								containsTableRule,
							)();
						}
						return true;
					};
				}
				return () => true;

			case AI_PASTE_MENU_REMIX_KEY:
				if (product === 'CONFLUENCE') {
					return () => {
						const isComment = getIsComment();
						// Remix is hidden in Confluence Comments — the existing action buttons
						// (Improve Writing / Fix Spelling / Make Longer) cover its role there.
						if (isComment) {
							return true;
						}
						// Non-comment Confluence: Stories 5.2 + 5.4 — mixed-node (medium and large).
						return allRules(
							minCharsRule(100),
							hideIfSingleNodeRule,
							isSingleLinkRule,
							disabled,
							notProseRule,
							containsTableRule,
						)();
					};
				}
				return () => true;

			case AI_PASTE_MENU_ADD_SUMMARY_KEY:
				if (product === 'CONFLUENCE') {
					return () => {
						if (
							!expValEqualsNoExposure('confluence_editor_paste_3p_link_actions_menu', 'isEnabled', true)
						) {
							return true;
						}
						return rules.notSingleLinkRule();
					};
				}
				return () => true;

			case AI_PASTE_MENU_ASK_ROVO_KEY:
				if (product === 'CONFLUENCE') {
					// Ask Rovo is shown for: (1) single-link paste (regardless of length),
					// or (2) large prose paste (≥ 600 chars).
					return () => {
						if (!notSingleLinkRule()) {
							// IS a single link.
							if (
								!expValEqualsNoExposure(
									'confluence_editor_paste_3p_link_actions_menu',
									'isEnabled',
									true,
								)
							) {
								return true;
							}
							return allRules(disabled)();
						}
						// Not a single link — apply full large-paste rules (no single-link gate needed).
						return allRules(
							minCharsRule(600),
							disabled,
							notProseRule,
							containsTableRule,
							isSingleLinkRule,
						)();
					};
				}
				return () => true;

			default:
				return () => true;
		}
	};

	return [
		{
			type: 'menu-section',
			key: AI_PASTE_MENU_SECTION_KEY,
			parents: [
				{
					type: PASTE_MENU.type,
					key: PASTE_MENU.key,
					rank: PASTE_MENU_RANK[AI_PASTE_MENU_SECTION_KEY],
				},
			],
			component: AIPasteMenuSection,
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_ADD_SUMMARY_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: 0,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_ADD_SUMMARY_KEY),
			isAsyncHidden: smartlinkAsyncHidden,
			component: () => <SmartLinkAddSummaryPasteMenuItem aiGlobalOptIn={aiGlobalOptIn} api={api} />,
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_IMPROVE_WRITING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_IMPROVE_WRITING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={improveWriting()}
					icon={<AICommandIcon size="small" label="" />}
					testId="ai-paste-menu__improve-writing"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_FIX_SPELLING_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_FIX_SPELLING_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_FIX_SPELLING_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={fixSpellingAndGrammar()}
					icon={<AISpellcheckIcon size="small" label="" />}
					testId="ai-paste-menu__fix-spelling-and-grammar"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_ADD_POLISH_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_ADD_POLISH_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_ADD_POLISH_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={addPolishWithImproveFormatting()}
					icon={<AiGenerativeCleanupIcon size="small" label="" />}
					testId="ai-paste-menu__add-polish"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_MAKE_SHORTER_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					rank: configByKey[AI_PASTE_MENU_MAKE_SHORTER_KEY]?.rank ?? 0,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_MAKE_SHORTER_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={makeShorter()}
					icon={<AIShortenIcon size="small" label="" />}
					testId="ai-paste-menu__make-shorter"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_MAKE_LONGER_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					// Rank 5 — shown after Make Shorter (rank 4) in the menu.
					rank: 5,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_MAKE_LONGER_KEY),
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={makeLonger()}
					icon={<TextLengthenIcon size="small" label="" />}
					testId="ai-paste-menu__make-longer"
				/>
			),
		},
		{
			type: 'menu-item',
			key: AI_PASTE_MENU_ASK_ROVO_KEY,
			parents: [
				{
					type: 'menu-section',
					key: AI_PASTE_MENU_SECTION_KEY,
					// Rank 7 — shown last in the menu.
					rank: 7,
				},
			],
			isHidden: makeIsHiddenV2(AI_PASTE_MENU_ASK_ROVO_KEY),
			isAsyncHidden: smartlinkAsyncHidden,
			component: () => (
				<AIPasteMenuItem
					aiGlobalOptIn={aiGlobalOptIn}
					api={api}
					configItem={freeGenerate()}
					icon={<RovoIcon size="xxsmall" label="" />}
					testId="ai-paste-menu__ask-rovo"
				/>
			),
		},
	];
};
